// PhotoPicker.tsx — widget del step 'photos' (My photos).
// Qué hace: en el rail queda un disparador compacto con lo elegido; la elección
//   ocurre en PhotoModal, a pantalla grande. Elegir fotos es visual: en 392px
//   entraban miniaturas de 72px donde no se distingue una foto de otra.
// Qué exporta: PhotoPicker.
// Quién lo usa: NovahGenerateChat (cuando el image style es 'asset').

import { useEffect, useRef, useState } from 'react';
import { WidgetWrap, ConfirmBtn } from '../styled';
import { TriggerCard, TriggerStrip, TriggerText } from '../styleModalStyled';
import { PhotoModal } from './PhotoModal';

interface Props {
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  confirmPhotos: () => void;
  uploadPhoto: (file: File) => void;
  photoUploading: boolean;
  totalPosts: number;
}

export function PhotoPicker(props: Props) {
  const { myPhotos, selectedPhotoIds, confirmPhotos } = props;
  const [modalOpen, setModalOpen] = useState(false);

  // Se abre solo la primera vez que aparece el paso.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current) return;
    autoOpened.current = true;
    setModalOpen(true);
  }, []);

  const picked = myPhotos.filter((p) => selectedPhotoIds.includes(p.id));
  const n = picked.length;

  return (
    <WidgetWrap>
      <TriggerCard type="button" onClick={() => setModalOpen(true)}>
        {n > 0 && (
          <TriggerStrip>
            {picked.slice(0, 3).map((p) => <img key={p.id} src={p.url} alt="" loading="lazy" />)}
          </TriggerStrip>
        )}
        <TriggerText>
          {n > 0 ? `${n} photo${n !== 1 ? 's' : ''} picked` : 'Choose your photos'}
          <em>
            {n > 0
              ? 'Tap to change or upload more'
              : `${myPhotos.length} in this workspace — tap to open`}
          </em>
        </TriggerText>
      </TriggerCard>

      <ConfirmBtn onClick={confirmPhotos} disabled={n === 0} style={{ marginTop: 10 }}>
        {n === 0 ? 'Pick at least one photo' : `Use ${n} photo${n !== 1 ? 's' : ''}`}
      </ConfirmBtn>

      <PhotoModal {...props} open={modalOpen} onClose={() => setModalOpen(false)} />
    </WidgetWrap>
  );
}
